import React, { useMemo, useState, useEffect } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { getActiveTestimonials, Testimonial } from '../../services/testimonialService';

const Testimonials: React.FC = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);

  const fallbackTestimonials = useMemo<Testimonial[]>(
    () => [
      {
        name: 'Priya Sharma',
        location: 'Mumbai',
        rating: 5,
        comment:
          'The AI analysis was spot-on! I felt confident and stylish throughout my Paris trip. The color recommendations perfectly complemented my skin tone, and I received so many compliments.',
        isActive: true,
        createdAt: new Date() as any
      },
      {
        name: 'Rahul Gupta',
        location: 'Delhi',
        rating: 5,
        comment:
          'As someone who struggles with fashion, Suitcase Muse was a lifesaver. The day-by-day styling made packing so much easier, and I looked professional throughout my business meetings.',
        isActive: true,
        createdAt: new Date() as any
      },
      {
        name: 'Anita Patel',
        location: 'Bangalore',
        rating: 5,
        comment:
          'The trip-specific styling service exceeded my expectations! Every outfit was perfect for the Greek islands. The packing list was so organized, and I had the perfect look for every occasion.',
        isActive: true,
        createdAt: new Date() as any
      },
      {
        name: 'Vikram Singh',
        location: 'Pune',
        rating: 5,
        comment:
          'I was amazed by how the AI understood my adventure travel needs. The recommendations balanced style with functionality perfectly. I looked great in all my travel photos!',
        isActive: true,
        createdAt: new Date() as any
      }
    ],
    []
  );

  useEffect(() => {
    const loadTestimonials = async () => {
      try {
        const firestoreTestimonials = await getActiveTestimonials();
        setTestimonials(firestoreTestimonials.length > 0 ? firestoreTestimonials : fallbackTestimonials);
      } catch {
        setTestimonials(fallbackTestimonials);
      } finally {
        setLoading(false);
      }
    };
    loadTestimonials();
  }, [fallbackTestimonials]);

  useEffect(() => {
    if (testimonials.length > 0) {
      const timer = setInterval(() => {
        setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [testimonials.length]);

  const nextTestimonial = () => setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  const prevTestimonial = () => setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  const current = testimonials[currentTestimonial];

  if (loading) {
    return (
      <section className="py-20 bg-light-gray/50">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-coral mx-auto" />
        </div>
      </section>
    );
  }

  if (testimonials.length === 0 || !current) return null;

  return (
    <section className="relative py-20 sm:py-28 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-light-gray/50 via-white to-white" />
      <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-coral/[0.03] rounded-full blur-[100px]" />
      <div className="absolute bottom-0 right-1/3 w-[400px] h-[400px] bg-teal/[0.03] rounded-full blur-[80px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14 sm:mb-16">
          <h2 className="font-nunito font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-charcoal mb-4 sm:mb-6 px-4">
            Loved by <span className="text-gradient">Our Clients</span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            See why clients trust Suitcase Muse for their styling needs.
          </p>
        </div>

        {/* Main Testimonial */}
        <div className="relative max-w-4xl mx-auto">
          <div className="relative glass shadow-glass-lg rounded-3xl p-8 sm:p-10 md:p-14 text-center overflow-hidden">
            {/* Decorative quote marks */}
            <div className="absolute top-6 left-8 opacity-[0.06]">
              <Quote size={80} className="text-coral" />
            </div>
            <div className="absolute bottom-6 right-8 opacity-[0.06] rotate-180">
              <Quote size={80} className="text-teal" />
            </div>

            {/* Stars */}
            <div className="flex justify-center mb-6 gap-1">
              {[...Array(current.rating)].map((_, i) => (
                <Star key={i} size={22} className="text-golden fill-golden" />
              ))}
            </div>

            {/* Quote */}
            <blockquote className="text-lg sm:text-xl md:text-2xl text-charcoal font-medium leading-relaxed mb-8 max-w-3xl mx-auto">
              "{current.comment}"
            </blockquote>

            {/* Author */}
            <div className="flex flex-col items-center">
              {current.avatar ? (
                <img
                  src={current.avatar}
                  alt={current.name}
                  className="w-14 h-14 rounded-full object-cover border-2 border-coral/20 mb-3"
                />
              ) : (
                <div className="w-14 h-14 bg-gradient-to-br from-coral to-teal rounded-full flex items-center justify-center mb-3">
                  <span className="text-white font-bold text-lg">
                    {current.name.charAt(0)}
                  </span>
                </div>
              )}
              <h4 className="font-nunito font-bold text-lg text-charcoal">
                {current.name}
              </h4>
              <p className="text-gray-500 text-sm">{current.location}</p>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-center items-center mt-8 space-x-4">
            <button
              onClick={prevTestimonial}
              aria-label="Previous testimonial"
              className="w-11 h-11 glass shadow-glass rounded-full flex items-center justify-center text-charcoal hover:text-coral hover:shadow-glow-coral transition-all duration-300"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <div className="flex">
              {testimonials.map((_, index) => (
                // Padded wrapper keeps the visual dot small but gives each an
                // adequately sized touch target.
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  aria-label={`Go to testimonial ${index + 1}`}
                  aria-current={index === currentTestimonial}
                  className="p-2 flex items-center"
                >
                  <span
                    className={`block h-2.5 rounded-full transition-all duration-300 ${
                      index === currentTestimonial ? 'bg-coral w-8' : 'bg-gray-300 hover:bg-coral/50 w-2.5'
                    }`}
                  />
                </button>
              ))}
            </div>

            <button
              onClick={nextTestimonial}
              aria-label="Next testimonial"
              className="w-11 h-11 glass shadow-glass rounded-full flex items-center justify-center text-charcoal hover:text-coral hover:shadow-glow-coral transition-all duration-300"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
